import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface SegmentedOption<K extends string> {
  key: K;
  label: React.ReactNode;
}

interface SegmentedToggleProps<K extends string> {
  options: SegmentedOption<K>[];
  value: K;
  onChange: (key: K) => void;
  ariaLabel: string;
}

/**
 * Two-layer segmented control. The base layer is the real buttons in their
 * inactive colours. On top sits an identical, non-interactive copy painted in
 * the active colours and clipped to the selected option's rectangle. Moving
 * the selection animates the clip, so the pill, the background and the text
 * colour all travel together as one shape instead of two buttons swapping
 * styles. clip-path stays on the compositor: no layout work per frame.
 */
const SegmentedToggle = <K extends string>({ options, value, onChange, ariaLabel }: SegmentedToggleProps<K>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [clip, setClip] = useState('inset(0 100% 0 0 round 999px)');

  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const active = root.querySelector<HTMLElement>(`[data-seg-key="${value}"]`);
    if (!active) return;
    const left = active.offsetLeft;
    const right = root.clientWidth - (active.offsetLeft + active.offsetWidth);
    setClip(`inset(0 ${right}px 0 ${left}px round 999px)`);
  }, [value]);

  // Measure before paint so the first frame is already correct, and again
  // whenever the control resizes (web font swap, viewport change).
  useLayoutEffect(() => {
    measure();
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure]);

  const optionStyle: React.CSSProperties = {
    padding: '8px 20px',
    borderRadius: '999px',
    border: 'none',
    background: 'transparent',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  };

  const shell: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '999px',
  };

  return (
    <div
      ref={rootRef}
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        ...shell,
        position: 'relative',
        background: 'var(--v2-card)',
        border: '1px solid var(--v2-line)',
      }}
    >
      {options.map((option) => {
        const selected = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={selected}
            data-seg-key={option.key}
            className="press"
            onClick={() => onChange(option.key)}
            style={{ ...optionStyle, color: 'var(--v2-ink-muted)', cursor: 'pointer' }}
          >
            {option.label}
          </button>
        );
      })}

      {/* Active-colour copy, clipped to the selected option. */}
      <div
        aria-hidden="true"
        className="seg-thumb"
        style={{
          ...shell,
          position: 'absolute',
          inset: 0,
          background: 'var(--v2-primary)',
          clipPath: clip,
          WebkitClipPath: clip,
          pointerEvents: 'none',
        }}
      >
        {options.map((option) => (
          <span key={option.key} style={{ ...optionStyle, color: '#fff' }}>
            {option.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default SegmentedToggle;
